// src/components/DPI/DPIComponent.tsx
/* eslint-disable no-console */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Button, ProgressBar, Alert, Badge } from 'react-bootstrap';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';

type Profile = Schema['Profile']['type'];
type ProfileCreate = Schema['Profile']['createType'];
type ProfileUpdate = Schema['Profile']['updateType'];

type Question = {
  id: string;
  text: string;
  pair: 'EI' | 'SN' | 'TF' | 'JP';
  positiveLetter: 'E' | 'I' | 'S' | 'N' | 'T' | 'F' | 'J' | 'P';
};

const GOOGLE_GREEN = '#34A853';
const GOOGLE_BLUE  = '#4285F4';
const GOOGLE_YELLOW= '#FBBC05';
const GOOGLE_RED   = '#EA4335';

const CHOICES: { label: string; value: -2 | -1 | 0 | 1 | 2 }[] = [
  { label: 'Strongly Disagree', value: -2 },
  { label: 'Disagree',          value: -1 },
  { label: 'Neutral',           value: 0  },
  { label: 'Agree',             value: 1  },
  { label: 'Strongly Agree',    value: 2  },
];

const QUESTIONS: Question[] = [
  // E–I
  { id: 'q1', text: 'I gain energy from being around groups of people.', pair: 'EI', positiveLetter: 'E' },
  { id: 'q2', text: 'I prefer quiet time alone to recharge after socializing.',         pair: 'EI', positiveLetter: 'I' },
  { id: 'q3', text: 'I think out loud and enjoy spontaneous discussion.',              pair: 'EI', positiveLetter: 'E' },
  // S–N
  { id: 'q4', text: 'I trust concrete facts more than abstract ideas.',                pair: 'SN', positiveLetter: 'S' },
  { id: 'q5', text: 'I like to imagine possibilities and patterns beyond the data.',   pair: 'SN', positiveLetter: 'N' },
  { id: 'q6', text: 'I focus on present realities more than future potentials.',       pair: 'SN', positiveLetter: 'S' },
  // T–F
  { id: 'q7', text: 'I make decisions by analyzing logic and criteria.',               pair: 'TF', positiveLetter: 'T' },
  { id: 'q8', text: 'I prioritize people’s feelings when deciding.',                   pair: 'TF', positiveLetter: 'F' },
  { id: 'q9', text: 'I value being fair and consistent over accommodating exceptions.',pair: 'TF', positiveLetter: 'T' },
  // J–P
  { id: 'q10', text: 'I prefer clear plans and closure.',                              pair: 'JP', positiveLetter: 'J' },
  { id: 'q11', text: 'I like keeping options open and staying adaptable.',             pair: 'JP', positiveLetter: 'P' },
  { id: 'q12', text: 'I feel more comfortable with schedules than with spontaneity.',  pair: 'JP', positiveLetter: 'J' },
];

type Answers = Record<string, -2 | -1 | 0 | 1 | 2>;
type PairTotals = { EI: number; SN: number; TF: number; JP: number };

function computeDPI(answers: Answers): {
  type: string;
  totals: PairTotals;
  letters: { EI: 'E' | 'I'; SN: 'S' | 'N'; TF: 'T' | 'F'; JP: 'J' | 'P' };
} {
  const totals: PairTotals = { EI: 0, SN: 0, TF: 0, JP: 0 };

  for (const q of QUESTIONS) {
    const v = answers[q.id] ?? 0; // -2..+2
    const delta = v;
    switch (q.pair) {
      case 'EI': totals.EI += q.positiveLetter === 'E' ? delta : -delta; break;
      case 'SN': totals.SN += q.positiveLetter === 'S' ? delta : -delta; break;
      case 'TF': totals.TF += q.positiveLetter === 'T' ? delta : -delta; break;
      case 'JP': totals.JP += q.positiveLetter === 'J' ? delta : -delta; break;
    }
  }

  const EI = totals.EI >= 0 ? 'E' : 'I';
  const SN = totals.SN >= 0 ? 'S' : 'N';
  const TF = totals.TF >= 0 ? 'T' : 'F';
  const JP = totals.JP >= 0 ? 'J' : 'P';
  return { type: `${EI}${SN}${TF}${JP}`, totals, letters: { EI, SN, TF, JP } };
}

export default function DPIComponent() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<ReturnType<typeof computeDPI> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<'idle' | 'ok' | 'err'>('idle');
  const [error, setError] = useState<string | null>(null);

  const total = QUESTIONS.length;
  const progress = Math.round(((index + 1) / total) * 100);
  const current = QUESTIONS[index];
  const selected = current ? answers[current.id] ?? null : null;

  function choose(val: -2 | -1 | 0 | 1 | 2) {
    if (!current) return;
    setAnswers((a) => ({ ...a, [current.id]: val }));
  }
  function next() { if (index < total - 1) setIndex((i) => i + 1); }
  function back() { if (index > 0) setIndex((i) => i - 1); }

  const allAnswered = useMemo(
    () => QUESTIONS.every((q) => typeof answers[q.id] === 'number'),
    [answers]
  );

  async function finalizeAndSave() {
    setError(null);
    const computed = computeDPI(answers);
    setResult(computed);
    setSaving(true);
    setSaved('idle');

    try {
      const { userId } = await getCurrentUser(); // may be useful for 'sub' if you want to store it
      const attrs = await fetchUserAttributes();
      const email = attrs?.email ?? '';
      const emailLower = email.toLowerCase();

      // ---- Lookup Profile by emailLower (identifier) ----
      let profile: Profile | null = null;

      try {
        const maybeGet = (client.models as any)?.Profile?.get;
        if (typeof maybeGet === 'function') {
          const getRes = await maybeGet({ emailLower });
          profile = getRes?.data ?? null;
        }
      } catch {
        /* ignore and fallback to list */
      }

      if (!profile) {
        const listed = await client.models.Profile.list({
          filter: { emailLower: { eq: emailLower } },
          limit: 1,
        });
        profile = listed?.data?.[0] ?? null;
      }

      if (!profile) {
        // -------- CREATE (no `id`; identifier is emailLower) --------
        const createInput = {
          emailLower,
          email,
          dpiType: computed.type,
          // optionally store sub (Cognito userId) if your schema has `sub?: string`
          sub: userId,
        } as ProfileCreate;

        const created = await client.models.Profile.create(createInput);
        if (!created?.data) throw new Error('Failed to create profile.');
      } else {
        // -------- UPDATE (must include the identifier emailLower) --------
        const updateInput = {
          emailLower,          // identifier
          dpiType: computed.type,
        } as ProfileUpdate;

        const updated = await client.models.Profile.update(updateInput);
        if (!updated?.data) throw new Error('Failed to update profile.');
      }

      setSaved('ok');
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Failed to save DPI type.');
      setSaved('err');
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setIndex(0);
    setAnswers({});
    setResult(null);
    setSaved('idle');
    setError(null);
  }

  return (
    <Container className="d-flex justify-content-center align-items-start" style={{ minHeight: '80vh' }}>
      <Card style={{ maxWidth: 720, width: '100%', marginTop: 24, borderRadius: 16 }}>
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h4 className="mb-0">
              <Badge bg="" style={{ background: GOOGLE_BLUE, color: 'white' }}>Dfuse</Badge>{' '}
              <span style={{ color: GOOGLE_BLUE }}>Personality Indicator (DPI)</span>
            </h4>
            <Badge bg="" style={{ background: GOOGLE_GREEN, color: 'white' }}>
              {result ? 'Complete' : `Q ${index + 1} / ${total}`}
            </Badge>
          </div>

          {!result && (
            <>
              <ProgressBar now={progress} label={`${progress}%`} className="mb-3" />

              <Card className="mb-3" style={{ borderRadius: 14 }}>
                <Card.Body>
                  <div className="mb-2" style={{ fontWeight: 600, fontSize: 18 }}>
                    {current?.text}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {CHOICES.map((c) => (
                      <Button
                        key={c.value}
                        variant={selected === c.value ? 'primary' : 'outline-primary'}
                        onClick={() => choose(c.value)}
                      >
                        {c.label}
                      </Button>
                    ))}
                  </div>
                </Card.Body>
              </Card>

              <div className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={back} disabled={index === 0}>
                  Back
                </Button>
                {index < total - 1 ? (
                  <Button variant="primary" onClick={next} disabled={selected === null}>
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    onClick={finalizeAndSave}
                    disabled={!allAnswered || saving}
                    style={{ background: GOOGLE_GREEN, borderColor: GOOGLE_GREEN }}
                  >
                    {saving ? 'Saving…' : 'See Results & Save'}
                  </Button>
                )}
              </div>
            </>
          )}

          {result && (
            <>
              <Card className="mb-3" style={{ borderRadius: 14 }}>
                <Card.Body>
                  <div className="text-center">
                    <div style={{ fontSize: 14, color: '#666' }}>Your DPI Type</div>
                    <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: 2, color: GOOGLE_BLUE }}>
                      {result.type}
                    </div>
                    <div className="mt-2" style={{ fontSize: 14, color: '#666' }}>
                      Based on your responses across four dimensions
                    </div>
                  </div>

                  <hr />

                  <div className="mb-2" style={{ fontWeight: 600 }}>Breakdown</div>
                  <PairBar label="E vs I" left="E" right="I" total={result.totals.EI} />
                  <PairBar label="S vs N" left="S" right="N" total={result.totals.SN} />
                  <PairBar label="T vs F" left="T" right="F" total={result.totals.TF} />
                  <PairBar label="J vs P" left="J" right="P" total={result.totals.JP} />
                </Card.Body>
              </Card>

              {saved === 'ok' && (
                <Alert variant="success" className="mb-3" style={{ borderLeft: `4px solid ${GOOGLE_GREEN}` }}>
                  Saved to your profile as <strong>{result.type}</strong>.
                </Alert>
              )}
              {saved === 'err' && (
                <Alert variant="danger" className="mb-3" style={{ borderLeft: `4px solid ${GOOGLE_RED}` }}>
                  Could not save your DPI type. {error ? <span>{error}</span> : null}
                </Alert>
              )}

              <div className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={reset}>Retake</Button>
                <Button
                  variant="primary"
                  onClick={() => navigate(-1)}
                  style={{ background: GOOGLE_BLUE, borderColor: GOOGLE_BLUE }}
                >
                  Done
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

function PairBar(props: { label: string; left: string; right: string; total: number }) {
  const { label, left, right, total } = props;
  const clamped = Math.max(-10, Math.min(10, total));
  const percent = Math.round(((clamped + 10) / 20) * 100);
  const barColor = total >= 0 ? GOOGLE_BLUE : GOOGLE_YELLOW;

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between" style={{ fontSize: 13, color: '#555' }}>
        <span>{label}</span>
        <span>
          <Badge bg="" style={{ background: barColor, color: 'white' }}>
            {total >= 0 ? right : left} leaning
          </Badge>
        </span>
      </div>
      <div style={{ position: 'relative', height: 10, background: '#eee', borderRadius: 6 }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: 10,
            width: `${percent}%`,
            background: barColor,
            borderRadius: 6,
            transition: 'width 300ms ease',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: -18,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 10,
            color: '#999',
          }}
        >
          {left} &nbsp; | &nbsp; {right}
        </div>
      </div>
    </div>
  );
}
